import React, { useState, useEffect } from 'react';
import { Campaign } from '@restore/services-lib';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import SingleSelectDropdown from '../SingleSelectDropdown';
import { CampaignsContextProvider, useCampaignsContext } from 'pages/Campaigns/campaigns.context';

interface CampaignSingleSelectDropdownInterface {
  selectedCampaign?: Campaign;
  selectedCampaignId?: number;
  onChange: (selectedOption?: DropdownOption) => void;
  label?: string;
}

const getSelectedCampaign = (campaign?: Campaign) =>
  campaign && { id: campaign.id, name: campaign.name };
const getSelectedCampaignFromId = (
  availableCampaigns: Campaign[],
  campaignId?: number
): Campaign | undefined =>
  campaignId ? availableCampaigns.filter((c) => c.id === campaignId)[0] : undefined;

const CampaignSingleSelectDropdownComponent = ({
  selectedCampaign,
  selectedCampaignId,
  onChange,
  label,
}: CampaignSingleSelectDropdownInterface) => {
  const { campaigns: availableCampaigns } = useCampaignsContext();
  const [selectedOption, setSelectedOption] = useState<DropdownOption | undefined>(
    selectedCampaign
      ? getSelectedCampaign(selectedCampaign)
      : getSelectedCampaignFromId(availableCampaigns, selectedCampaignId)
  );

  useEffect(() => {
    setSelectedOption(
      selectedCampaign
        ? getSelectedCampaign(selectedCampaign)
        : getSelectedCampaignFromId(availableCampaigns, selectedCampaignId)
    );
  }, [selectedCampaign, selectedCampaignId, availableCampaigns]);

  const campaignsOptions: DropdownOption[] = availableCampaigns.map((campaign) => ({
    name: campaign.name,
    id: campaign.id,
  }));

  const handleChange = (newSelectedOption?: DropdownOption) => {
    setSelectedOption(newSelectedOption);
    onChange(newSelectedOption);
  };

  return (
    <SingleSelectDropdown
      fieldLabel={label ?? 'Campaign'}
      allOptions={campaignsOptions}
      fullWidth={true}
      selected={selectedOption}
      setSelected={handleChange}
      variant='outlined'
    />
  );
};

const CampaignSingleSelectDropdown = ({
  selectedCampaign,
  selectedCampaignId,
  onChange,
  label,
}: CampaignSingleSelectDropdownInterface) => (
  <CampaignsContextProvider>
    <CampaignSingleSelectDropdownComponent
      selectedCampaignId={selectedCampaignId}
      selectedCampaign={selectedCampaign}
      onChange={onChange}
      label={label}
    />
  </CampaignsContextProvider>
);

export default CampaignSingleSelectDropdown;
